import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { isNil } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { IntercomHelp } from '../../../../../../../components/help/intercom-help';
import { IntercomArticle } from '../../../../../../../config/intercom';
import i18n from '../../../../../../../i18n';
import { FlowItem } from '../../../../../shared/model/flow.types';
import { FlowItemStitch } from './flow-item-stitch';

export interface StitchPathDetails {
  item: FlowItem;
  cb?: () => void;
}

interface StitchPathProps {
  isOpen: boolean;
  stitchDetails: StitchPathDetails | undefined;
  onClose: () => void;
}

const StitchPathModal = (props: StitchPathProps): ReactElement => {
  const { isOpen, stitchDetails, onClose } = props;

  const startItem = useMemo(() => {
    return stitchDetails?.item;
  }, [stitchDetails?.item]);

  const onStitchConverted = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <IonModal isOpen={isOpen}>
      <>
        <IonHeader>
          <IonToolbar>
            <IonButtons
              slot={'start'}
              onClick={() => {
                onClose();
              }}
            >
              <IonButton>
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{i18n.t('common:stitch_settings')}</IonTitle>
            <div slot="end">
              <IntercomHelp articleId={IntercomArticle.ENHANCED_STITCH_WELD} />
            </div>
          </IonToolbar>
        </IonHeader>
        {startItem && (
          <FlowItemStitch
            startItem={startItem}
            onStitchConverted={onStitchConverted}
          />
        )}
      </>
    </IonModal>
  );
};

interface UseStitchPathResult {
  stitchPathComponent: ReactElement;
  openStitchPath: (item: FlowItem, cb?: () => void) => void;
  closeStitchPath: () => void;
}

const useStitchPath = (): UseStitchPathResult => {
  const [stitchPathDetails, setStitchPathDetails] = useState<
    StitchPathDetails | undefined
  >();

  const openStitchPath = useCallback((item: FlowItem, cb?: () => void) => {
    setStitchPathDetails({ item, cb });
  }, []);

  const closeStitchPath = useCallback(() => {
    if (stitchPathDetails?.cb) {
      stitchPathDetails.cb();
    }
    setStitchPathDetails(undefined);
  }, [stitchPathDetails]);

  const stitchPathComponent = useMemo(() => {
    return (
      <StitchPathModal
        isOpen={!isNil(stitchPathDetails)}
        stitchDetails={stitchPathDetails}
        onClose={closeStitchPath}
      />
    );
  }, [closeStitchPath, stitchPathDetails]);

  const result = useMemo(() => {
    return {
      stitchPathComponent,
      openStitchPath,
      closeStitchPath,
    };
  }, [closeStitchPath, openStitchPath, stitchPathComponent]);

  return result;
};

export { useStitchPath };
